"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";

type UseHashScrollOptions = {
  /**
   * Offset in pixels to apply when scrolling (useful for fixed headers)
   * @default 0
   */
  offset?: number;
  /**
   * Whether to use smooth scrolling
   * @default true
   */
  smooth?: boolean;
  /**
   * Callback fired when the element is scrolled to
   */
  onScrollTo?: () => void;
  /**
   * Whether this anchor should be active (useful for conditional rendering)
   * @default true
   */
  enabled?: boolean;
};

type RefCallback = (element: HTMLElement | null) => void;

/**
 * Hook for components to register module ID(s) and handle hash-based scrolling
 *
 * @param moduleIds - One or more module IDs to register
 * @param options - Optional configuration options (must be last argument if provided)
 * @returns A single ref callback (if one ID) or array of ref callbacks (if multiple IDs)
 *          Each ref automatically sets the element's id attribute
 *
 * @example
 * ```tsx
 * // Single anchor
 * function MyComponent() {
 *   const anchorRef = useHashScroll("my-section");
 *   return <section ref={anchorRef}>Content here</section>;
 * }
 *
 * // Multiple anchors
 * function TabsComponent() {
 *   const [newsRef, blogRef, insightsRef] = useHashScroll("news", "blog", "insights");
 *   return (
 *     <>
 *       <section ref={newsRef}>News</section>
 *       <section ref={blogRef}>Blog</section>
 *       <section ref={insightsRef}>Insights</section>
 *     </>
 *   );
 * }
 *
 * // With options
 * const anchorRef = useHashScroll("my-section", { offset: 80 });
 * const [ref1, ref2] = useHashScroll("section1", "section2", { offset: 80 });
 * ```
 */

// Single anchor without options
export function useHashScroll(moduleId: string): RefCallback;

// Single anchor with options
export function useHashScroll(moduleId: string, options: UseHashScrollOptions): RefCallback;

// Multiple anchors without options
export function useHashScroll(...moduleIds: string[]): RefCallback[];

// Multiple anchors with options (options must be last)
export function useHashScroll(
  moduleId1: string,
  moduleId2: string,
  ...rest: [...moduleIds: string[], options: UseHashScrollOptions]
): RefCallback[];

// Implementation
export function useHashScroll(...args: unknown[]): RefCallback | RefCallback[] {
  // Extract anchor IDs and options from arguments
  const lastArg = args[args.length - 1];
  const hasOptions =
    lastArg && typeof lastArg === "object" && !("length" in lastArg) && !Array.isArray(lastArg);
  const options = (hasOptions ? lastArg : {}) as UseHashScrollOptions | undefined;
  const moduleIds = (hasOptions ? args.slice(0, -1) : args) as string[];

  const { offset = 0, smooth = true, onScrollTo, enabled = true } = options || {};

  const elementRefs = useRef<(HTMLElement | null)[]>([]);
  const isSingle = moduleIds.length === 1;

  // Initialize refs array
  useEffect(() => {
    elementRefs.current = moduleIds.map(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleIds.length]);

  // Create callback refs for each anchor ID
  const createRef = useCallback((index: number, moduleId: string) => {
    return (element: HTMLElement | null) => {
      elementRefs.current[index] = element;
      if (element && moduleId) {
        element.id = moduleId;
      }
    };
  }, []);

  const moduleIdsString = useMemo(() => moduleIds.join(","), [moduleIds]);

  const refs = useMemo(
    () => moduleIds.map((moduleId, index) => createRef(index, moduleId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [moduleIdsString, moduleIds.length, createRef],
  );

  const moduleIdsRef = useRef(moduleIds);

  // Update ref when moduleIds change
  useEffect(() => {
    moduleIdsRef.current = moduleIds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleIdsString, moduleIds.length]);

  useEffect(() => {
    if (!enabled || moduleIds.length === 0) return;

    const checkAndScroll = () => {
      const hash = window.location.hash.slice(1);
      if (!hash) return false;

      const targetIndex = moduleIdsRef.current.findIndex((id) => id === hash);
      if (targetIndex === -1) return false;

      const element = elementRefs.current[targetIndex];
      if (element) {
        const elementTop = element.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementTop - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: smooth ? "smooth" : "auto",
        });

        onScrollTo?.();
        return true;
      }
      return false;
    };

    // Check on mount
    checkAndScroll();

    // Listen for hash changes
    const handleHashChange = () => {
      checkAndScroll();
    };

    window.addEventListener("hashchange", handleHashChange);

    // Also check periodically in case content loads asynchronously
    // This helps with components that render after initial page load
    let retryCount = 0;
    const maxRetries = 50; // 5 seconds at 100ms intervals
    const intervalId = setInterval(() => {
      if (checkAndScroll() || retryCount >= maxRetries) {
        clearInterval(intervalId);
      }
      retryCount++;
    }, 100);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      clearInterval(intervalId);
    };
  }, [moduleIdsString, moduleIds.length, offset, smooth, onScrollTo, enabled]);

  return isSingle ? refs[0] : refs;
}
